import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth.jsx";

/**
 * Global Pipeline Switcher context (v5.7 Addition 1).
 *
 * - Fetches active pipelines for the current tenant.
 * - Tracks the current selection.
 * - Persists selection server-side on the user record.
 * - Exposes { pipelines, pipelineId, setPipelineId } to descendants.
 *
 * For tenants with multi_pipeline_enabled=false the sidebar switcher UI is
 * hidden, but this context still holds the single pipeline so every screen
 * can source pipeline_id from one place.
 */
const PipelineCtx = createContext(null);

export function PipelineProvider({ children }) {
  const { user, tenant } = useAuth();
  const [pipelines, setPipelines] = useState([]);
  const [pipelineId, setPipelineIdState] = useState(null);

  const reloadPipelines = useCallback(async () => {
    if (!user || user.role === "superadmin") {
      setPipelines([]);
      setPipelineIdState(null);
      return;
    }
    const { data } = await api.get("/pipelines");
    setPipelines(data || []);
    // Preserve current if still present, else last_selected, else oldest
    setPipelineIdState((cur) => {
      if (cur && data.some((p) => p.pipeline_id === cur)) return cur;
      const last = user.last_selected_pipeline_id;
      if (last && data.some((p) => p.pipeline_id === last)) return last;
      return data[0]?.pipeline_id ?? null;
    });
  }, [user]);

  useEffect(() => {
    reloadPipelines();
  }, [reloadPipelines]);

  const setPipelineId = useCallback(
    async (nextId) => {
      if (!nextId || nextId === pipelineId) return;
      setPipelineIdState(nextId);
      try {
        await api.post("/me/pipeline-context", { pipeline_id: nextId });
      } catch {
        /* non-fatal — selection still applies for this session */
      }
    },
    [pipelineId]
  );

  const value = {
    pipelines,
    pipelineId,
    setPipelineId,
    multiEnabled: !!tenant?.multi_pipeline_enabled,
    reloadPipelines,
  };
  return <PipelineCtx.Provider value={value}>{children}</PipelineCtx.Provider>;
}

export const usePipelineCtx = () => useContext(PipelineCtx) || {
  pipelines: [], pipelineId: null, setPipelineId: () => {}, multiEnabled: false, reloadPipelines: () => {},
};
